<template>
  <div class="flex flex-col gap-8">
    <div class="flex flex-col gap-2">
      <b class="text-md font-bold text-neutral-700">
        {{ t('overview') }}
      </b>
      <div class="grid min-w-[700px] grid-cols-3 gap-4 text-[0.9rem]">
        <InfusePropertyValue :title="t('property.auditLog.id')" :title-icon="{ type: 'far', name: 'hashtag' }">
          {{ auditLog?.id }}

          <InfuseCopy v-slot="scope">
            <font-awesome-icon
              class="ml-1.5 cursor-pointer text-neutral-500 hover:text-neutral-600"
              :icon="['far', 'copy']"
              @click="scope.copy(`${auditLog?.id}`)"
            />
          </InfuseCopy>
        </InfusePropertyValue>

        <InfusePropertyValue
          :title="t('property.auditLog.correlationId')"
          :title-icon="{ type: 'far', name: 'hashtag' }"
        >
          {{ auditLog?.correlationId }}

          <InfuseCopy v-slot="scope">
            <font-awesome-icon
              class="ml-1.5 cursor-pointer text-neutral-500 hover:text-neutral-600"
              :icon="['far', 'copy']"
              @click="scope.copy(auditLog?.correlationId || '')"
            />
          </InfuseCopy>
        </InfusePropertyValue>

        <InfusePropertyValue :title="t('property.auditLog.createdAt')" :title-icon="{ type: 'far', name: 'clock' }">
          {{ formatDate(auditLog?.createdAt) }}
        </InfusePropertyValue>
      </div>
    </div>

    <div class="flex flex-col gap-2">
      <b class="text-md font-bold text-neutral-700">
        {{ t('user') }}
      </b>
      <div class="grid min-w-[700px] grid-cols-3 gap-4 text-[0.9rem]">
        <InfusePropertyValue :title="t('property.auditLog.userId')" :title-icon="{ type: 'fas', name: 'user' }">
          {{ auditLog?.userId }}

          <InfuseCopy v-slot="scope">
            <font-awesome-icon
              class="ml-1.5 cursor-pointer text-neutral-500 hover:text-neutral-600"
              :icon="['far', 'copy']"
              @click="scope.copy(auditLog?.userId)"
            />
          </InfuseCopy>
        </InfusePropertyValue>

        <InfusePropertyValue :title="t('property.auditLog.ip')" :title-icon="{ type: 'fas', name: 'globe' }">
          <InfuseTag :inline="true" variant="neutral" class="font-medium">
            <font-awesome-icon
              :icon="[
                'fal',
                isLocalIp(auditLog?.ipAddress) // Local testing etc.
                  ? 'home'
                  : isInternalIp(auditLog?.ipAddress) // Internal network ranges.
                  ? 'cloud'
                  : 'globe' // An actual IP from outside. Probably.
              ]"
              class="mr-2"
            />

            {{ auditLog?.ipAddress || '-' }}
          </InfuseTag>

          <InfuseCopy v-if="auditLog?.ipAddress" v-slot="scope">
            <font-awesome-icon
              class="ml-1.5 cursor-pointer text-neutral-500 hover:text-neutral-600"
              :icon="['far', 'copy']"
              @click="scope.copy(auditLog.ipAddress)"
            />
          </InfuseCopy>
        </InfusePropertyValue>
      </div>
    </div>

    <div class="flex flex-col gap-2">
      <b class="text-md font-bold text-neutral-700">
        {{ t('action') }}
      </b>
      <div class="grid min-w-[700px] grid-cols-3 gap-4 text-[0.9rem]">
        <InfusePropertyValue :title="t('property.auditLog.objectType')" :title-icon="{ type: 'far', name: 'cube' }">
          {{ auditLog?.objectType }}
        </InfusePropertyValue>

        <InfusePropertyValue :title="t('property.auditLog.objectId')" :title-icon="{ type: 'far', name: 'hashtag' }">
          {{ auditLog?.objectId || '-' }}

          <InfuseCopy v-slot="scope">
            <font-awesome-icon
              class="ml-1.5 cursor-pointer text-neutral-500 hover:text-neutral-600"
              :icon="['far', 'copy']"
              @click="scope.copy(auditLog?.objectId || '-')"
            />
          </InfuseCopy>
        </InfusePropertyValue>

        <InfusePropertyValue :title="t('property.auditLog.action')" :title-icon="{ type: 'fas', name: 'gear' }">
          {{ auditLog?.action || '-' }}
        </InfusePropertyValue>

        <InfusePropertyValue :title="t('property.auditLog.success')">
          <font-awesome-icon
            :icon="['fal', auditLog?.success ? 'circle-check' : 'circle-xmark']"
            class="h-5 w-5"
            :class="auditLog?.success ? 'text-success-500' : 'text-error-500'"
          />
        </InfusePropertyValue>
      </div>

      <div class="mt-2">
        <span class="text-[0.65rem] uppercase text-neutral-400">
          {{ t('property.auditLog.description') }}
        </span>
        <InfuseJsonCodeBlock :format="false" :payload="auditLog?.description || '-'" />
      </div>
    </div>

    <div class="flex flex-col gap-2">
      <b class="text-md font-bold text-neutral-700">
        {{ t('http') }}
      </b>
      <div class="grid min-w-[700px] grid-cols-3 gap-4 text-[0.9rem]">
        <InfusePropertyValue
          :title="t('property.auditLog.httpMethod')"
          :title-icon="{ type: 'fas', name: 'code-simple' }"
        >
          {{ auditLog?.httpMethod }}
        </InfusePropertyValue>

        <InfusePropertyValue :title="t('property.auditLog.uri')" :title-icon="{ type: 'fas', name: 'link' }">
          {{ auditLog?.uri }}
        </InfusePropertyValue>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { AuditLog } from '@infuse/audit-log/store';
import { InfuseCopy, InfuseJsonCodeBlock, InfusePropertyValue, InfuseTag } from '@infuse/ui/components';
import { formatDate } from '@infuse/ui/utils';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

interface Props {
  auditLog: AuditLog;
}

withDefaults(defineProps<Props>(), {});

const isLocalIp = (ipAddress?: string): boolean => {
  if (!ipAddress) {
    return false;
  }
  return ipAddress === '127.0.0.1' || ipAddress === '0:0:0:0:0:0:0:1';
};

const isInternalIp = (ipAddress?: string): boolean => {
  if (!ipAddress) {
    return false;
  }
  return (
    ipAddress.startsWith('10.') || // Probably UI, or some app
    ipAddress.startsWith('192.168.') || // Other internal network IP ranges
    ipAddress.startsWith('172.16.')
  );
};
</script>

<i18n>
{
  "en": {
    "overview": "Overview",
    "user": "User",
    "action": "Action",
    "http": "HTTP",
    "property": {
      "auditLog": {
        "id": "Log ID",
        "correlationId": "Correlation ID",
        "createdAt": "Logged at",
        "userId": "User ID",
        "ip": "IP address",
        "objectType": "Object Type",
        "objectId": "Object ID",
        "action": "Action",
        "description": "Description",
        "success": "Successful",
        "httpMethod": "HTTP Method",
        "uri": "URI"
      }
    }
  },
  "nl": {
    "overview": "Overzicht",
    "user": "Gebruiker",
    "action": "Actie",
    "http": "HTTP",
    "property": {
      "auditLog": {
        "id": "Log ID",
        "correlationId": "Correlatie-ID",
        "createdAt": "Gelogd op",
        "userId": "Gebruikers-ID",
        "ip": "IP-adres",
        "objectType": "Objecttype",
        "objectId": "Object-ID",
        "action": "Actie",
        "description": "Beschrijving",
        "success": "Succesvol",
        "httpMethod": "HTTP-methode",
        "uri": "URI"
      }
    }
  }
}
</i18n>
